/**
 * useSession Hook
 * Manages the BRR session lifecycle
 */

import { useState, useCallback, useEffect } from 'react';
import { SessionAuditClient } from '../services';
import { BRRSession } from '../services/types';
import { isValidResumeStage } from '../utils/stages';

interface UseSessionOptions {
  batchId: string | null;
  sessionClient: SessionAuditClient;
  autoCreate?: boolean;
}

interface ResumedSession {
  sessionId: string;
  batchId: string;
  currentPage: string;
}

interface UseSessionReturn {
  session: BRRSession | null;
  sessionId: string | null;
  loading: boolean;
  error: Error | null;
  createSession: () => Promise<string | null>;
  updateSessionPage: (page: string) => Promise<void>;
  updateLastSaved: () => Promise<void>;
  endSession: () => Promise<void>;
  resumeSession: () => Promise<ResumedSession | null>;
}

/**
 * Hook for managing session tracking
 *
 * @example
 * const { session, sessionId, createSession, updateLastSaved } = useSession({
 *   batchId: 'B-2026-0147',
 *   sessionClient: sessionApi,
 *   autoCreate: true
 * });
 */
export const useSession = ({
  batchId,
  sessionClient,
  autoCreate = false
}: UseSessionOptions): UseSessionReturn => {
  const [session, setSession] = useState<BRRSession | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createSession = useCallback(async (): Promise<string | null> => {
    if (!batchId) {
      setError(new Error('Cannot create session without batch ID'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await sessionClient.createSession({
        batchId,
        currentPage: 'form'
      });

      setSessionId(result.id);

      // Fetch the created session
      const createdSession = await sessionClient.getSession(result.id);
      if (createdSession) {
        setSession(createdSession);
      }

      return result.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Failed to create session:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [batchId, sessionClient]);

  const updateSessionPage = useCallback(async (page: string) => {
    if (!sessionId) {
      console.warn('Cannot update session page: no session ID');
      return;
    }

    try {
      await sessionClient.updateSession(sessionId, {
        currentPage: page
      });
    } catch (err) {
      console.error('Failed to update session page:', err);
    }
  }, [sessionId, sessionClient]);

  const updateLastSaved = useCallback(async () => {
    if (!sessionId) {
      console.warn('Cannot update last saved: no session ID');
      return;
    }

    try {
      await sessionClient.updateSession(sessionId, {
        lastSavedAt: new Date()
      });
    } catch (err) {
      console.error('Failed to update last saved:', err);
    }
  }, [sessionId, sessionClient]);

  const endSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      await sessionClient.deleteSession(sessionId);
      setSession(null);
      setSessionId(null);
    } catch (err) {
      console.error('Failed to end session:', err);
    }
  }, [sessionId, sessionClient]);

  const resumeSession = useCallback(async (): Promise<ResumedSession | null> => {
    try {
      const sessions = await sessionClient.getInProgressSessions();
      if (sessions.length === 0) {
        return null;
      }

      // Use the most recently updated session (already ordered by lastsavedat desc)
      const latest = sessions[0];
      const cols = sessionClient.columns;
      const page = latest[cols.currentpage];

      if (!page || !isValidResumeStage(page)) {
        return null;
      }

      const id = latest[cols.sessionid];
      const batch = latest[cols.batchid];

      setSession(latest);
      setSessionId(id);

      return { sessionId: id, batchId: batch, currentPage: page };
    } catch (err) {
      console.error('Failed to resume session:', err);
      return null;
    }
  }, [sessionClient]);

  // Auto-create session on mount if enabled
  useEffect(() => {
    if (autoCreate && batchId && !sessionId) {
      void createSession();
    }
  }, [autoCreate, batchId, sessionId, createSession]);

  return {
    session,
    sessionId,
    loading,
    error,
    createSession,
    updateSessionPage,
    updateLastSaved,
    endSession,
    resumeSession
  };
};
